import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import TrajectoryRow from './TrajectoryRow';
import { recordKey } from '../../lib/trajectory-record';

const ROW_HEIGHT = 38;   // must match .traj-row in App.css
const OVERSCAN = 10;

/**
 * Hand-rolled windowing. Realistic sessions here are hundreds of records, not
 * millions, and a fixed row height removes the need for the measurement cache a
 * general-purpose virtualizer would bring — so this stays ~40 lines instead of
 * a dependency.
 */
function useWindowedRows(scrollRef, count) {
  const [range, setRange] = useState({ start: 0, end: Math.min(count, 60) });

  const recompute = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const first = Math.floor(el.scrollTop / ROW_HEIGHT);
    const visible = Math.ceil(el.clientHeight / ROW_HEIGHT);
    setRange({
      start: Math.max(0, first - OVERSCAN),
      end: Math.min(count, first + visible + OVERSCAN),
    });
  }, [scrollRef, count]);

  useEffect(() => { recompute(); }, [recompute]);
  return [range, recompute];
}

export default function TrajectoryLedger({
  records, total, hasMoreBefore, selectedId, onSelect, onLoadOlder, loadingOlder,
}) {
  const scrollRef = useRef(null);
  const [range, recompute] = useWindowedRows(scrollRef, records.length);
  const followRef = useRef(true);
  const lastTailRef = useRef(null);
  const prependRef = useRef({ firstId: null, count: 0, hadButton: false });

  // Loading an older page grows the content above the viewport, which would
  // otherwise shove the row the user is reading downward. Compensate before the
  // browser paints so the visible record stays exactly where it was — the whole
  // point of paging backwards on demand instead of all at once.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const prev = prependRef.current;
    const firstId = records[0]?.id ?? null;
    if (el && prev.firstId !== null && firstId !== prev.firstId && records.length > prev.count) {
      const addedRows = records.length - prev.count;
      const buttonDelta = (prev.hadButton ? 1 : 0) - (hasMoreBefore ? 1 : 0);
      el.scrollTop += (addedRows - buttonDelta) * ROW_HEIGHT;
    }
    prependRef.current = { firstId, count: records.length, hadButton: hasMoreBefore };
  }, [records, hasMoreBefore]);

  // Follow the tail only while the user is already at it. Scrolling up to
  // inspect an earlier row must not be interrupted by new records arriving.
  const onScroll = () => {
    const el = scrollRef.current;
    if (el) followRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    recompute();
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || records.length === 0) return;
    const tail = records[records.length - 1].id;
    const first = lastTailRef.current === null;
    if (tail === lastTailRef.current) return;
    lastTailRef.current = tail;
    if (first || followRef.current) el.scrollTop = el.scrollHeight;
  }, [records]);

  const visible = records.slice(range.start, range.end);

  return (
    <div className="traj-ledger" ref={scrollRef} onScroll={onScroll} role="table"
         aria-rowcount={total}>
      {hasMoreBefore && (
        <button
          className="traj-load-older"
          disabled={loadingOlder}
          onClick={onLoadOlder}
        >
          {loadingOlder ? 'Загрузка…' : `Показать более раннее (всего ${total})`}
        </button>
      )}
      <div style={{ height: range.start * ROW_HEIGHT }} />
      {visible.map(rec => (
        <TrajectoryRow
          key={recordKey(rec)}
          rec={rec}
          selected={rec.id === selectedId}
          onSelect={onSelect}
        />
      ))}
      <div style={{ height: (records.length - range.end) * ROW_HEIGHT }} />
    </div>
  );
}
